import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
    Table, 
    Card, 
    Button, 
    Input, 
    Space, 
    Tag, 
    Modal, 
    Form, 
    Typography, 
    message, 
    Popconfirm,
    Tooltip,
    Switch
} from 'antd';
import { 
    SearchOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    PlusOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const InvestmentTypes = () => {
    const { loading, getInvestmentTypes, createInvestmentType, updateInvestmentType, deleteInvestmentType } = useAdmin();
    const [types, setTypes] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [form] = Form.useForm();

    const loadTypes = useCallback(async () => {
        const result = await getInvestmentTypes();
        if (result.success) {
            setTypes(result.data);
        }
    }, [getInvestmentTypes]);

    useEffect(() => {
        loadTypes();
    }, [loadTypes]);

    const handleAdd = () => {
        setEditingType(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingType(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        const result = await deleteInvestmentType(id);
        if (result.success) {
            message.success('Type d\'investissement supprimé');
            loadTypes();
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            let result;

            if (editingType) {
                result = await updateInvestmentType(editingType.id, values);
            } else {
                result = await createInvestmentType(values);
            }

            if (result.success) {
                message.success(editingType ? 'Mis à jour avec succès' : 'Créé avec succès');
                setIsModalVisible(false);
                loadTypes();
            }
        } catch (error) {
            console.error('Validate Failed:', error);
        }
    };

    const filteredData = useMemo(() => {
        return types.filter(item => 
            item.name.toLowerCase().includes(searchText.toLowerCase()) || 
            (item.description && item.description.toLowerCase().includes(searchText.toLowerCase()))
        );
    }, [types, searchText]);

    const columns = [
        {
            title: 'Nom',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong style={{ color: '#6366f1' }}>{text}</Text>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Statut',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active) => <Tag color={active ? 'green' : 'red'}>{active ? 'ACTIF' : 'INACTIF'}</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Modifier">
                        <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{ color: '#6366f1' }} />
                    </Tooltip>
                    <Popconfirm
                        title="Supprimer ce type ? Les plans liés resteront mais sans catégorie."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Oui"
                        cancelText="Non"
                    >
                        <Tooltip title="Supprimer">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="admin-investment-types-inner">
            <div className="admin-page-header" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0, color: '#f1f5f9' }}>Types d'Investissement</Title>
                    <Text type="secondary">Gestion des catégories de packs.</Text>
                </div>
                <Space>
                    <Button icon={<SyncOutlined spin={loading} />} onClick={loadTypes}>Actualiser</Button>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleAdd}
                    >
                        Ajouter un Type
                    </Button>
                </Space>
            </div>

            <Card className="glass-card" style={{ background: 'rgba(30, 41, 59, 0.4)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Input
                        placeholder="Rechercher un type..."
                        prefix={<SearchOutlined />}
                        style={{ width: 300, borderRadius: 8 }}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                    />
                </div>

                <Table 
                    columns={columns} 
                    dataSource={filteredData} 
                    rowKey="id"
                    loading={loading}
                    className="admin-table"
                />
            </Card>

            <Modal
                title={editingType ? "Modifier le Type" : "Ajouter un Type"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                okText="Enregistrer"
                cancelText="Annuler"
                centered
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Nom du Type"
                        rules={[{ required: true, message: 'Le nom est requis' }]}
                    >
                        <Input placeholder="ex: Trading IA, Immobilier, etc." />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea rows={4} placeholder="Détails sur ce type d'investissement..." />
                    </Form.Item>

                    <Form.Item
                        name="icon_url"
                        label="Lien de l'icône (Optionnel)"
                    >
                        <Input placeholder="https://..." />
                    </Form.Item>

                    <Form.Item
                        name="is_active"
                        label="Statut Actif"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            <style dangerouslySetInnerHTML={{ __html: `
                .admin-investment-types-inner { animation: fadeIn 0.5s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}} />
        </div>
    );
};

export default InvestmentTypes;
